import { useEffect, useMemo, useState } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { apiUrl } from "../../utils/api";

type AvailableRisk = {
  id: number;
  name: string;
  riskType: string;
  characteristics: Record<string, string>;
};

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    rowGap: "20px",
  },
  hint: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.muted,
    fontStyle: "italic",
    backgroundColor: BRAND.bg,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
    ...shorthands.borderRadius("8px"),
    ...shorthands.border("1px", "dashed", BRAND.borderDashed),
  },
  section: {
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
  },
  sectionTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.1rem",
    color: BRAND.ink,
    margin: 0,
  },
  sectionSub: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    color: BRAND.muted,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  card: {
    appearance: "none",
    cursor: "pointer",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "16px",
    paddingRight: "16px",
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    columnGap: "14px",
    width: "100%",
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("2px", "solid", BRAND.border),
    transitionProperty: "border-color, background-color, box-shadow",
    transitionDuration: "150ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
      backgroundColor: "#f9f5fa",
    },
    ":disabled": {
      cursor: "not-allowed",
      opacity: 0.45,
    },
  },
  cardActive: {
    ...shorthands.borderColor(BRAND.purple),
    backgroundColor: "#f5eef6",
    boxShadow: "0 4px 14px rgba(77,32,81,0.16)",
  },
  iconBox: {
    width: "40px",
    height: "40px",
    backgroundColor: "#f5eef6",
    color: BRAND.purple,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...shorthands.borderRadius("10px"),
  },
  iconBoxActive: {
    backgroundColor: BRAND.purple,
    color: BRAND.white,
  },
  body: {
    display: "flex",
    flexDirection: "column",
    rowGap: "2px",
    minWidth: 0,
    flexGrow: 1,
  },
  title: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    fontWeight: 700,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  addr: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.82rem",
    color: BRAND.muted,
  },
  radio: {
    width: "22px",
    height: "22px",
    flexShrink: 0,
    ...shorthands.borderRadius("999px"),
    ...shorthands.border("2px", "solid", "#cdc6d3"),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.white,
  },
  radioActive: {
    ...shorthands.borderColor(BRAND.purple),
    backgroundColor: BRAND.purple,
  },
  radioDot: {
    width: "10px",
    height: "10px",
    backgroundColor: BRAND.white,
    ...shorthands.borderRadius("999px"),
  },
  check: {
    width: "22px",
    height: "22px",
    flexShrink: 0,
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("2px", "solid", "#cdc6d3"),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.white,
    color: BRAND.white,
  },
  checkActive: {
    ...shorthands.borderColor(BRAND.purple),
    backgroundColor: BRAND.purple,
  },
  loading: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: BRAND.muted,
    paddingTop: "12px",
    paddingBottom: "12px",
  },
  error: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.9rem",
    color: "#a1331c",
  },
});

const HouseIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 11l9-7 9 7" />
    <path d="M5 10v10h14V10" />
    <path d="M10 20v-6h4v6" />
  </svg>
);

const formatAddress = (c: Record<string, string>): string => {
  const street = c.street ?? "";
  const plz = c.postalCode ?? "";
  const city = c.city ?? "";
  return [street, [plz, city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
};

const RisikoAuswahlSection = ({
  contactId,
  hauptrisikoId,
  temporaereRisikoIds,
  onChangeHaupt,
  onChangeTemporaere,
}: {
  contactId: number | null;
  hauptrisikoId: number | null;
  temporaereRisikoIds: number[];
  onChangeHaupt: (id: number | null) => void;
  onChangeTemporaere: (ids: number[]) => void;
}) => {
  const styles = useStyles();
  const [risks, setRisks] = useState<AvailableRisk[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!contactId) {
      setRisks([]);
      return;
    }
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(apiUrl(`risk-objects/by-contact/${contactId}`));
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const arr: Array<{
          id: number;
          name?: string;
          riskType?: string;
          characteristics?: Array<{ nameBezeichnung?: string; riskCharacteristicValue?: string }>;
        }> = Array.isArray(data) ? data : [];
        const mapped: AvailableRisk[] = arr.map((r) => {
          const chars: Record<string, string> = {};
          for (const c of r.characteristics ?? []) {
            if (c.nameBezeichnung) chars[c.nameBezeichnung] = String(c.riskCharacteristicValue ?? "");
          }
          return {
            id: r.id,
            name: r.name ?? "",
            riskType: r.riskType ?? "",
            characteristics: chars,
          };
        });
        const immoRisks = mapped.filter((r) => (r.riskType ?? "").toLowerCase().includes("immo"));
        setRisks(immoRisks);
      } catch (e) {
        console.error(e);
        setError("Risiken konnten nicht geladen werden.");
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [contactId]);

  const tempCandidates = useMemo(
    () => risks.filter((r) => r.id !== hauptrisikoId),
    [risks, hauptrisikoId],
  );

  if (!contactId) {
    return (
      <div className={styles.hint}>
        Bitte wählen Sie zuerst einen Kontakt im vorherigen Schritt.
      </div>
    );
  }

  if (loading) {
    return <div className={styles.loading}>Lade Risiken…</div>;
  }
  if (error) {
    return <div className={styles.error}>{error}</div>;
  }
  if (risks.length === 0) {
    return (
      <div className={styles.hint}>
        Für diesen Kontakt liegen aktuell keine Immobilien-Risikoobjekte vor.
      </div>
    );
  }

  const toggleTemp = (id: number) => {
    if (temporaereRisikoIds.includes(id)) {
      onChangeTemporaere(temporaereRisikoIds.filter((x) => x !== id));
    } else {
      onChangeTemporaere([...temporaereRisikoIds, id]);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Hauptrisiko</h3>
        <span className={styles.sectionSub}>
          Genau ein Risikoobjekt — Hauptobjekt des Vertrags. Eine neue Auswahl ersetzt die vorherige.
        </span>
        <div className={styles.list}>
          {risks.map((r) => {
            const active = hauptrisikoId === r.id;
            return (
              <button
                key={r.id}
                type="button"
                className={mergeClasses(styles.card, active && styles.cardActive)}
                onClick={() => onChangeHaupt(active ? null : r.id)}
                aria-pressed={active}
              >
                <div className={mergeClasses(styles.iconBox, active && styles.iconBoxActive)}>
                  <HouseIcon />
                </div>
                <div className={styles.body}>
                  <span className={styles.title}>
                    {r.name || formatAddress(r.characteristics) || `Risiko ${r.id}`}
                  </span>
                  <span className={styles.addr}>{formatAddress(r.characteristics) || `Risiko-ID ${r.id}`}</span>
                </div>
                <span className={mergeClasses(styles.radio, active && styles.radioActive)} aria-hidden>
                  {active && <span className={styles.radioDot} />}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className={styles.section}>
        <h3 className={styles.sectionTitle}>Temporär vermietete Risiken (optional)</h3>
        <span className={styles.sectionSub}>
          Weitere Risikoobjekte dieses Kontakts, die nur temporär vermietet werden. Pro temporärem Risiko wird im
          Schritt „Versicherte Gefahren" der Teil-/Komplettausschluss erfasst.
        </span>
        {tempCandidates.length === 0 ? (
          <div className={styles.hint}>Keine weiteren Risikoobjekte verfügbar.</div>
        ) : (
          <div className={styles.list}>
            {tempCandidates.map((r) => {
              const active = temporaereRisikoIds.includes(r.id);
              return (
                <button
                  key={r.id}
                  type="button"
                  className={mergeClasses(styles.card, active && styles.cardActive)}
                  onClick={() => toggleTemp(r.id)}
                  aria-pressed={active}
                >
                  <div className={mergeClasses(styles.iconBox, active && styles.iconBoxActive)}>
                    <HouseIcon />
                  </div>
                  <div className={styles.body}>
                    <span className={styles.title}>
                      {r.name || formatAddress(r.characteristics) || `Risiko ${r.id}`}
                    </span>
                    <span className={styles.addr}>{formatAddress(r.characteristics) || `Risiko-ID ${r.id}`}</span>
                  </div>
                  <span className={mergeClasses(styles.check, active && styles.checkActive)} aria-hidden>
                    {active && (
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8.5l3 3 7-7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RisikoAuswahlSection;
